import { switchLightModuleApi, updateLightLanApi } from '@/requestPub/lightApi';
import { UdpReceiveData } from '@/types/global';
import { action_Light } from './serialPort';

/** 灯控状态常量 */
export const LIGHT_STATUS = {
    ON: 1, // 通电
    OFF: 2 // 断电
} as const;

/**
 * Electron API 接口定义
 */
interface ElectronLightAPI {
    findGateways: (options: { ip?: string; data: string }, callback: (info: any) => void) => void;
    sendUdp: (options: { ip: string; port?: number; data: string }) => void;
    stopfindgateways: () => void;
}

/**
 * 获取灯控插件实例（兼容 Electron 和原生插件）
 */
export const getLightPlugin = (): {
    findGateways?: (options: { ip?: string; data: string }, callback: (info: any) => void) => void;
    sendUdp?: (options: { ip: string; port?: number; data: string }) => void;
    stopfindgateways?: () => void;
} => {
    // 优先使用 Electron API
    if (typeof window !== 'undefined' && (window as any).electronAPI) {
        return (window as any).electronAPI as ElectronLightAPI;
    }

    // #ifdef APP-PLUS
    try {
        const plugin = uni.requireNativePlugin('xsyz-shyd-light');
        // @ts-ignore
        if (plugin) {
            return plugin;
        }
    } catch {
        return {};
    }
    // #endif
    // uniapp web-view 嵌套环境
    if (getApp()?.globalData?.platform?.nvue) {
        return {
            sendUdp: (options: UdpCommandOptions) => {
                uni.webView?.postMessage?.({
                    data: {
                        action: 'doLight',
                        value: options
                    }
                });
            }
        };
    }

    return {};
};

/**
 * 灯控通道信息
 * @property light_control - 灯控设备信息（IP、端口）
 * @property config - 通道配置（编码）
 * @property light_control_id - 灯控设备 ID
 * @property id - 通道编号 ID
 */
interface LightControlChannel {
    light_control: {
        ip: string;
        port?: number;
    };
    config: {
        no: string;
    };
    light_control_id: number;
    id: number;
}

/**
 * 开关灯指令参数
 * @property ip - 灯控设备 IP
 * @property port - 端口，默认 18888
 * @property code - 编码（格式如 "01 05 00 00 FF 00 8C 3A"）
 * @property showToast - 是否显示 Toast，默认 true
 */
interface LightCommandOptions {
    ip: string;
    port?: number;
    code?: string;
    showToast?: boolean;
    serial_port?: string;
    channel_number?: number;
}

/**
 * UDP 指令参数
 * @property ip - 目标 IP
 * @property port - 目标端口，默认 18888
 * @property data - UDP 数据内容
 */
interface UdpCommandOptions {
    ip: string;
    port?: number;
    data: string;
    value?: string;
    light_control_id?: number;
}

/**
 * 计算灯编码
 * @description 每 8 路为一组，如 1-8 路为第 1 组，9-16 路为第 2 组，以此类推
 * @param light 当前路数（从 1 开始）
 * @param isOn 是否开灯（"FF" 为开，"00" 为关）
 * @returns 编码字符串，如 "01 05 00 00 FF 00 8C 3A"
 */
export const getLightCode = (light: number, isOn: string): string => {
    const groupNum = Math.floor((light - 1) / 8) + 1;
    const lightNum = Math.floor((light - 1) % 8);
    return `${groupNum.toString().padStart(2, '0')} 05 00 ${lightNum.toString().padStart(2, '0')} ${isOn} 00 8C 3A`;
};

/**
 * 切换灯状态编码
 * @param code 原始编码（包含 "FF" 占位）
 * @param isOn 是否开灯
 * @returns 替换后的编码
 */
export const switchLight = (code: string, isOn: boolean): string => {
    return code.replace('FF', isOn ? 'FF' : '00');
};

/**
 * 查找网关设备（兼容 Electron 和原生插件）
 * 优先级：window.electronAPI.findGateways > Light.findgateways
 * @param options 查找参数
 * @param callback 回调函数
 */
export const findGateways = (options: { ip?: string; data: string }, callback: (info: any) => void): void => {
    const plugin = getLightPlugin();

    // 优先使用 findGateways（Electron API）
    if (plugin.findGateways) {
        plugin.findGateways(options, callback);
        return;
    }

    // 降级使用 findgateways（原生插件）
    // @ts-ignore
    if (plugin.findgateways) {
        // @ts-ignore
        plugin.findgateways(options, callback);
        return;
    }

    // app webview嵌套不提示
    if (!getApp()?.globalData?.platform?.nvue) {
        uni.$emit('showToast', { title: '网页端无法使用灯控，请下载桌面端或者收银端', type: 'error' });
    }
};

/**
 * 停止查找网关（兼容 Electron 和原生插件）
 */
export const stopFindGateways = (): void => {
    const plugin = getLightPlugin();
    if (plugin.stopfindgateways) {
        plugin.stopfindgateways?.();
        return;
    }
};

/**
 * 发送 UDP 指令到灯控设备（兼容 Electron 和原生插件）
 * 优先级：window.electronAPI.sendUdp > Light.sendUdp
 * @param options UDP 指令参数
 * @returns 是否发送成功
 */
export const sendUdp = (options: { ip: string; port?: number; data: string }): boolean => {
    try {
        const plugin = getLightPlugin();
        if (plugin.sendUdp) {
            console.log('%c [ sss ]-172', 'font-size:13px; background:pink; color:#bf2c9f;', options);
            console.log('%c [ options ]-175', 'font-size:13px; background:pink; color:#bf2c9f;', plugin.sendUdp);
            plugin.sendUdp({
                ip: options.ip,
                port: options.port || 18888,
                data: options.data
            });
            return true;
        }
        // app webview嵌套不提示
        if (!getApp()?.globalData?.platform?.nvue) {
            uni.$emit('showToast', { title: '网页端无法使用灯控，请下载桌面端或者收银端', type: 'error' });
        }
        return false;
    } catch (error) {
        console.error('发送 UDP 指令失败:', error);
        return false;
    }
};
/**
 * 是否支持 UDP 指令发送
 */
export const isUdpSupported = (): boolean => {
    // const isControl = Object.keys(getLightPlugin()).length > 0;
    // console.log('isControl', isControl);
    // if (isControl) {
    //     return true;
    // }
    // // app webview嵌套不提示
    // if (!getApp()?.globalData?.platform?.nvue) {
    //     uni.$emit('showToast', { title: '网页端无法使用灯控，请下载桌面端或者收银端', type: 'error' });
    // }
    // return false;
    return true;
};

/**
 * 发送 UDP 指令到灯控设备（封装层，兼容旧代码）
 * @param options UDP 指令参数
 * @returns 是否发送成功
 */
export const sendUdpCommand = (options: UdpCommandOptions): boolean => {
    return sendUdp({
        ip: options.ip,
        port: options.port,
        data: options.data
    });
};

/**
 * 发送 UDP 指令到灯控设备
 * @param options UDP 指令参数
 * @returns 是否发送成功
 */
export const LightCommand = (options: UdpCommandOptions): Promise<any> => {
    try {
        return new Promise((resolve) => {
            let timer: any;
            timer = setTimeout(() => {
                if (options.light_control_id) {
                    updateLightLanApi({
                        light_control_id: options.light_control_id as number,
                        response: ''
                    });
                }
                resolve({ data: '' });
                timer = null;
            }, 2000);

            // @ts-ignore
            findGateways(
                {
                    ip: options.ip,
                    data: options.value as string
                },
                (info: UdpReceiveData) => {
                    // 这个是自己收到的值 需过滤
                    if (info?.data != 'FBAAC2FF') {
                        if (options.light_control_id) {
                            updateLightLanApi({
                                light_control_id: options.light_control_id as number,
                                response: info.data as string
                            });
                        }
                        if (timer) {
                            clearTimeout(timer);
                            timer = null;
                        }
                        resolve(info);
                    }
                }
            );
        });
    } catch (error) {
        console.error('发送 UDP 指令失败:', error);
        return Promise.reject(error);
    }
};

/**
 * 发送开关灯指令
 * @param isOn 是否开灯
 * @param options 指令参数，包含 IP、端口、编码等
 */
export const sendLightCommand = (isOn: boolean, options?: LightCommandOptions): void => {
    const action = isOn ? '开灯' : '关灯';
    console.log(`准备发送${action}指令`, options);
    if (options?.serial_port) {
        console.log('发送串口数据', options?.serial_port);
        action_Light({
            port: options?.serial_port,
            open: isOn,
            number: options?.channel_number
        });
        return;
    }
    if (!options?.code) return;

    sendUdpCommand({
        ip: options.ip,
        port: options.port,
        data: switchLight(options.code, isOn)
    });
};

/**
 * 从桌台信息提取灯控配置并校验
 * @param tableInfo 桌台信息
 * @returns 校验通过返回灯控配置，否则返回 null
 */
const extractLightConfig = (tableInfo: any): LightControlChannel | null => {
    const channel = tableInfo?.light_control_channel;
    if (!channel?.light_control || !channel?.config) return null;
    // if (!channel.light_control.ip) return null;
    return channel;
};

/**
 * 开关灯公共方法
 * @param isOn 是否开灯
 * @param tableInfo 桌台信息
 * @param duration 定时关闭时长（分钟），仅开灯时有效；0 或 null 表示常开/常关
 */
export const toggleLight = async (isOn: boolean, tableInfo: any, duration?: number | null): Promise<void> => {
    const channel = extractLightConfig(tableInfo);
    if (!channel) return;

    const { light_control, config, light_control_id, id } = channel;
    const action = isOn ? '打开' : '关闭';

    try {
        console.log(`${action}灯`, light_control.ip, light_control.port, config.no);

        // 先调用 API 更新灯状态
        await switchLightModuleApi({
            light_control_id,
            channel_number_id: id,
            light_status: isOn ? LIGHT_STATUS.ON : LIGHT_STATUS.OFF,
            duration: isOn ? duration : undefined
        });
        // sendLightCommand(isOn, {
        //     ip: light_control.ip,
        //     port: light_control.port,
        //     code: config.no
        // });
    } catch (error) {
        console.error(`${action}灯失败:`, error);
    }
};

/**
 * 关闭指定桌台的灯
 * @param tableInfo 桌台信息
 */
export const turnOffLight = (tableInfo: any): Promise<void> => toggleLight(false, tableInfo);

/**
 * 打开指定桌台的灯
 * @param tableInfo 桌台信息
 * @param duration 持续时间（分钟），0(临时常开)或 null(普通常开)
 */
export const turnOnLight = (tableInfo: any, duration?: number | null): Promise<void> => toggleLight(true, tableInfo, duration);
